import { Injectable } from '@angular/core';

import { environment } from '@env/environment';
import { CountryModel } from '@models/country-model';
import { CustomerModel } from '@models/customer';
import { OrderInfoModel } from '@models/order-info.model';
import { plainToClass } from 'class-transformer';
import { Subject } from 'rxjs';
import { HttpService } from './http.service';
import { UserService } from './user.service';
import { CustomerStatusEnum } from '@app/customer-selector/customer-selector.component';

@Injectable({
	providedIn: 'root'
})
export class CustomerDataService {

	public customersWithOrdersDataChanged = new Subject<Array<CustomerModel>>();

	constructor(
		private userService: UserService,
		private httpService: HttpService,
	) {
	}

	public async getCustomersData(onlyCustomersWithOrders: boolean, useAllCustomerStates: boolean = false): Promise<Array<CustomerModel>> {
		const user = await this.userService.GetUser();

		if (user && (user.canOrderOnBehalf || user.canOrderOnBehalfOfChainCustomer)) {
			let result;

			if (useAllCustomerStates) {
				const searchParams: CustomerStatusEnum[] = [];
				searchParams.push(CustomerStatusEnum.active);
				searchParams.push(CustomerStatusEnum.blocked);
				searchParams.push(CustomerStatusEnum.nonDistributed);
				searchParams.push(CustomerStatusEnum.nonPartner);
				result = await this.getCustomers(onlyCustomersWithOrders, true, searchParams);
			}
			else {
				result = await this.getCustomers(onlyCustomersWithOrders);
			}

			return result.map(x => plainToClass(CustomerModel, x));
		}
		else {
			return [];
		}
	}

	public async getShopInfoCustomers(): Promise<Array<CustomerModel>> {
		let result = await this._getShopInfoCustomers();
		return result.map(x => plainToClass(CustomerModel, x));
	}

	public async getCountryCodes(): Promise<Array<CountryModel>> {
		let result = await this._getCountryCodes();
		return result.map(x => plainToClass(CountryModel, x));
	}

	public async GetCustomerToken(customerModel: CustomerModel): Promise<string> {
		return (await this.httpService.postWithOfflineHandling<any>(`${environment.dataUrl}/token/newcustomer`, customerModel)).AccessToken;
	}

	// public async getCustomerData(customerNo: string, addActiveParam: boolean = false): Promise<CustomerModel> {
	// 	const allCustomers = await this.getCustomersData(false, addActiveParam);
	// 	const customer = allCustomers.find(customer => customer.No == customerNo);
	// 	return plainToClass(CustomerModel, customer);
	// }

	public async getCustomerByCode(clientCustomerCode?: string): Promise<CustomerModel> {
		const customer = await this.httpService.getDataWithOfflineCheck<CustomerModel>(`${environment.dataUrl}/Account/CustomerByCode/${clientCustomerCode}`);
		return customer;
	}

	public async getCustomerByNo(clientCustomerNo: string): Promise<CustomerModel> {
		const customer = await this.httpService.getDataWithOfflineCheck<CustomerModel>(`${environment.dataUrl}/Account/CustomerByNo/${clientCustomerNo}`);
		return customer
	}

	public async getCustomers(
		onlyOnesWithOrderItemsOn: boolean = false,
		useServerCache: boolean = true,
		searchParam?: CustomerStatusEnum[],
		isPrecious?: boolean,
		offset?: number,
		limit?: number,
		searchString?: string
	): Promise<Array<CustomerModel>> {
		let queryString: string = `?OnlyWithCurrentOrders=${onlyOnesWithOrderItemsOn}`;
		queryString += !useServerCache ? '&useServerCache=false' : '';
		queryString += searchParam ? '&customerStatusList=' + searchParam.map(s => s).join(',') : '';
		queryString += isPrecious ? '&isPrecious=true' : '';
		queryString += searchString ? '&search=' + searchString : '';
		queryString += offset ? '&offset=' + offset : '';
		queryString += limit ? '&limit=' + limit : '';

		let result: Array<CustomerModel> = await this.httpService.getDataWithOfflineCheck<Array<CustomerModel>>(`${environment.dataUrl}/Account/customers/${queryString}`);

		return result.map(x => plainToClass(CustomerModel, x));
	}

	private async _getShopInfoCustomers(): Promise<Array<CustomerModel>> {
		let result: Array<CustomerModel>;

		result = await this.httpService.getDataWithOfflineCheck<Array<CustomerModel>>(`${environment.dataUrl}/Account/ShopInfoCustomers`);

		return result.map(x => plainToClass(CustomerModel, x));
	}

	private async _getCountryCodes(): Promise<Array<CountryModel>> {
		let result: Array<CountryModel>;

		result = await this.httpService.getDataWithOfflineCheck<Array<CountryModel>>(`${environment.dataUrl}/Account/CustomersCountryCodes`);

		return result.map(x => plainToClass(CountryModel, x));
	}

	public async getCustomersOrders(customerNo: string): Promise<Array<OrderInfoModel>> {
		return await this.httpService.getDataWithOfflineCheck<Array<OrderInfoModel>>(`${environment.dataUrl}/ordersbycustomerno/${customerNo}`);
	}

}
